import { classCodeService, StudentSummary } from './classCodeService';
import { userDataService } from './userDataService';
import { PointManagementService } from './pointManagementService';
import { PointTypeManagementService } from './pointTypeManagementService';
import { defaultSetService } from './defaultSetService';
import { ItemCardService } from './itemCardService';
import { boardService } from './boardService';
import { normalizeLevelUpPoints } from '../utils/levelUpRewardHelper';
import {
  MemberPageCacheScope,
  studentMemberPageCache
} from './studentMemberPageCache';
import type { StudentAssignmentHistory } from '../types/assignmentPointsMemo';
import type { PointRecord } from '../types/pointManagement';

export type CachedClassMeta = {
  levelUpPoints: number;
  memberGroupNames: Record<string, string>;
};

export type CachedDefaultSetAccess = {
  routineSectionEnabled: boolean;
  storeIndividualPurchaseEnabled: boolean;
};

export type CachedStudentProfile = {
  summary: StudentSummary;
  profileTableTotalPoints: number | null;
  levelUpPoints: number;
};

export type CachedRoutineTodayItem = {
  id: string;
  routineAssignmentId: string;
  dateId: string;
  title: string;
  status: string;
  basePoints: number;
  completionRate?: number;
  confirmedCount?: number;
  totalDates?: number;
  todayCount?: number;
};

export type CachedRoutineToday = {
  list: CachedRoutineTodayItem[];
  initialStatuses: Record<string, string>;
};

export type CachedItemCards = {
  allItemCards: any[];
  studentItemCards: any[];
  itemCardSet: Awaited<ReturnType<ItemCardService['loadItemCardSet']>>;
};

export function buildMemberPageScope(params: {
  userId: string;
  classId: string;
  memberGroup: string;
  studentId: string;
  classCode?: string;
}): MemberPageCacheScope {
  return {
    userId: params.userId,
    classId: params.classId,
    memberGroup: params.memberGroup,
    studentId: params.studentId,
    classCode: params.classCode
  };
}

function classMetaScope(scope: MemberPageCacheScope): MemberPageCacheScope {
  return {
    userId: scope.userId,
    classId: scope.classId,
    memberGroup: scope.memberGroup,
    studentId: '_class',
    classCode: scope.classCode
  };
}

async function fetchLevelUpPoints(userId: string, classId: string, sessionFallback: number): Promise<number> {
  try {
    const levelSettings = await userDataService.loadLevelSettings(userId, classId);
    return normalizeLevelUpPoints(levelSettings?.levelUpPoints, sessionFallback);
  } catch {
    try {
      const pointTypeService = new PointTypeManagementService(userId, classId);
      const pointTypeData = await pointTypeService.getPointTypeData();
      return normalizeLevelUpPoints(pointTypeData.levelUpPoints, sessionFallback);
    } catch {
      return sessionFallback;
    }
  }
}

export const studentMemberPageData = {
  async loadClassMeta(
    scope: MemberPageCacheScope,
    options?: {
      force?: boolean;
      sessionLevelUpPoints?: number;
      sessionMemberGroupNames?: Record<string, string>;
    }
  ): Promise<CachedClassMeta> {
    const metaScope = classMetaScope(scope);
    const sessionFallback = normalizeLevelUpPoints(options?.sessionLevelUpPoints, 50);
    const sessionNames = options?.sessionMemberGroupNames ?? {};

    return studentMemberPageCache.getOrFetch(
      metaScope,
      'classMeta',
      async () => {
        let memberGroupNames = { ...sessionNames };
        if (scope.classCode && Object.keys(memberGroupNames).length === 0) {
          const meta = await classCodeService.getClassEntryMeta(scope.classCode);
          if (meta.success && meta.memberGroupNames) {
            memberGroupNames = meta.memberGroupNames;
          }
        }
        const levelUpPoints = await fetchLevelUpPoints(scope.userId, scope.classId, sessionFallback);
        return { levelUpPoints, memberGroupNames };
      },
      { force: options?.force }
    );
  },

  async loadDefaultSetAccess(
    scope: MemberPageCacheScope,
    options?: { force?: boolean }
  ): Promise<CachedDefaultSetAccess> {
    const metaScope = classMetaScope(scope);
    return studentMemberPageCache.getOrFetch(
      metaScope,
      'defaultSet',
      async () => {
        try {
          const defaultSet = await defaultSetService.loadDefaultSet(scope.userId, scope.classId);
          return {
            routineSectionEnabled:
              defaultSet?.routineSectionEnabled !== undefined ? defaultSet.routineSectionEnabled : true,
            storeIndividualPurchaseEnabled:
              defaultSet?.storeIndividualPurchaseEnabled !== undefined
                ? defaultSet.storeIndividualPurchaseEnabled
                : true
          };
        } catch {
          return { routineSectionEnabled: true, storeIndividualPurchaseEnabled: true };
        }
      },
      { force: options?.force }
    );
  },

  async loadPointsTable(
    scope: MemberPageCacheScope,
    options?: { force?: boolean }
  ) {
    const metaScope = classMetaScope(scope);
    return studentMemberPageCache.getOrFetch(
      metaScope,
      'pointsTable',
      async () => {
        const classMembers = await userDataService.loadClassMembers(
          scope.userId,
          scope.classId,
          scope.memberGroup
        );
        const { PointsQueryService } = await import('./pointsQueryService');
        const pointsQueryService = new PointsQueryService(
          scope.userId,
          scope.classId,
          scope.memberGroup
        );
        return pointsQueryService.getPointsTableData(classMembers);
      },
      { force: options?.force }
    );
  },

  async loadStudentProfile(
    scope: MemberPageCacheScope,
    options?: { force?: boolean; sessionLevelUpPoints?: number }
  ): Promise<CachedStudentProfile> {
    return studentMemberPageCache.getOrFetch(
      scope,
      'studentProfile',
      async () => {
        const classMeta = await this.loadClassMeta(scope, {
          force: options?.force,
          sessionLevelUpPoints: options?.sessionLevelUpPoints
        });
        const pointsTableData = await this.loadPointsTable(scope, { force: options?.force });
        const targetStudent = pointsTableData.students.find((s) => s.studentId === scope.studentId);

        const classMembers = await userDataService.loadClassMembers(
          scope.userId,
          scope.classId,
          scope.memberGroup
        );
        const member = classMembers.find((m) => m.id === scope.studentId);
        if (!member) {
          throw new Error('멤버를 찾을 수 없습니다.');
        }

        const summary = await classCodeService.getStudentSummary(
          scope.userId,
          scope.classId,
          member,
          scope.memberGroup
        );

        const pointManagementService = new PointManagementService(
          scope.userId,
          scope.classId,
          scope.memberGroup
        );
        const pointHistory = await pointManagementService.getStudentPointHistory(scope.studentId!);
        summary.positivePoints = pointHistory
          .filter((p) => p.pointCategory === 'positive')
          .reduce((sum, p) => sum + (p.pointValue || 0), 0);

        if (targetStudent) {
          summary.assignmentPoints = targetStudent.assignments.reduce(
            (sum, assignment) => sum + (assignment.points || 0),
            0
          );
        }

        summary.totalPoints =
          targetStudent?.totalPoints ?? summary.assignmentPoints + summary.positivePoints;

        return {
          summary,
          profileTableTotalPoints: targetStudent?.totalPoints ?? null,
          levelUpPoints: classMeta.levelUpPoints
        };
      },
      { force: options?.force }
    );
  },

  async loadAssignmentHistory(
    scope: MemberPageCacheScope,
    options?: { force?: boolean }
  ): Promise<StudentAssignmentHistory | null> {
    return studentMemberPageCache.getOrFetch(
      scope,
      'assignmentHistory',
      async () => {
        const pointsTableData = await this.loadPointsTable(scope);
        return (
          pointsTableData.students.find((s) => s.studentId === scope.studentId) ?? null
        );
      },
      { force: options?.force }
    );
  },

  async loadPositiveHistory(
    scope: MemberPageCacheScope,
    options?: { force?: boolean }
  ): Promise<PointRecord[]> {
    return studentMemberPageCache.getOrFetch(
      scope,
      'positiveHistory',
      async () => {
        const pointManagementService = new PointManagementService(
          scope.userId,
          scope.classId,
          scope.memberGroup
        );
        const rewardPointsData = await pointManagementService.getStudentPointHistory(scope.studentId!);
        return rewardPointsData
          .filter((point) => point.pointCategory === 'positive')
          .sort((a, b) => {
            const getTimestamp = (recordedDate: any): number => {
              if (!recordedDate) return Date.now();
              if (recordedDate instanceof Date) return recordedDate.getTime();
              if (
                recordedDate &&
                typeof recordedDate === 'object' &&
                'toDate' in recordedDate &&
                typeof recordedDate.toDate === 'function'
              ) {
                return (recordedDate as { toDate: () => Date }).toDate().getTime();
              }
              try {
                return new Date(recordedDate).getTime();
              } catch {
                return Date.now();
              }
            };
            return getTimestamp(b.recordedDate) - getTimestamp(a.recordedDate);
          });
      },
      { force: options?.force }
    );
  },

  async loadPendingAssignments(scope: MemberPageCacheScope, options?: { force?: boolean }) {
    return studentMemberPageCache.getOrFetch(
      scope,
      'pendingAssignments',
      async () =>
        classCodeService.getStudentPendingAssignments(
          scope.userId,
          scope.classId,
          scope.studentId!,
          scope.memberGroup
        ),
      { force: options?.force }
    );
  },

  async loadRoutineToday(scope: MemberPageCacheScope, options?: { force?: boolean }): Promise<CachedRoutineToday> {
    return studentMemberPageCache.getOrFetch(
      scope,
      'routineToday',
      async () => {
        const list = await classCodeService.getStudentTodayRoutineAssignments(
          scope.userId,
          scope.classId,
          scope.studentId!,
          scope.memberGroup
        );

        const { routineAssignmentService } = await import('./routineAssignmentService');
        const { RoutineAssignmentConsolidatedService } = await import('./routineAssignmentConsolidatedService');

        const listWithCompletion = await Promise.all(
          list.map(async (r) => {
            try {
              const routines = await routineAssignmentService.loadRoutineAssignments(
                scope.userId,
                scope.classId,
                scope.memberGroup
              );
              const routine = routines.find((rt) => rt.id === r.routineAssignmentId);
              if (!routine) return r;

              const generateRoutineDates = (rt: {
                startDate?: unknown;
                routineDays?: number[];
                dueDate?: unknown;
              }) => {
                const dates: Array<{ dateId: string; date: Date }> = [];
                if (!rt.startDate || !rt.routineDays || !rt.dueDate) return dates;
                const startDate = new Date(rt.startDate as string | Date);
                const endDate = new Date(rt.dueDate as string | Date);
                endDate.setHours(23, 59, 59, 999);
                const currentDate = new Date(startDate);
                while (currentDate <= endDate) {
                  if (rt.routineDays!.includes(currentDate.getDay())) {
                    const dateId = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
                    dates.push({ dateId, date: new Date(currentDate) });
                  }
                  currentDate.setDate(currentDate.getDate() + 1);
                }
                return dates;
              };

              const routineDates = generateRoutineDates(routine);
              const totalDates = routineDates.length;
              const today = new Date();
              const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
              const todayCount = routineDates.filter((d) => {
                const dateOnly = new Date(d.date.getFullYear(), d.date.getMonth(), d.date.getDate());
                return dateOnly <= todayOnly;
              }).length;

              const consolidatedService = new RoutineAssignmentConsolidatedService(
                scope.userId,
                scope.classId,
                scope.memberGroup
              );
              const result = await consolidatedService.loadRoutineAssignmentWithStatuses(routine.id);
              const dateAssignments = result.dateStatuses || [];
              let confirmedCount = 0;
              dateAssignments.forEach((assignment: { studentStatuses?: { studentId: string; status?: string }[] }) => {
                const studentStatus = assignment.studentStatuses?.find(
                  (s) => s.studentId === scope.studentId
                );
                if (studentStatus?.status === 'confirmed') confirmedCount += 1;
              });

              const completionRate =
                totalDates > 0 ? Number(((confirmedCount / totalDates) * 100).toFixed(2)) : 0;

              return { ...r, completionRate, confirmedCount, totalDates, todayCount };
            } catch (error) {
              console.error(`루틴 과제 ${r.routineAssignmentId} 도달률 계산 실패:`, error);
              return r;
            }
          })
        );

        const initialStatuses: Record<string, string> = {};
        listWithCompletion.forEach((r) => {
          initialStatuses[r.id] = r.status;
        });

        return { list: listWithCompletion, initialStatuses };
      },
      { force: options?.force }
    );
  },

  async loadGeneralNotices(
    scope: MemberPageCacheScope,
    options?: { force?: boolean }
  ): Promise<any[]> {
    return studentMemberPageCache.getOrFetch(
      scope,
      'generalNotices',
      async () => {
        const notes = await boardService.getGeneralNoticeNotesForMember(
          scope.userId,
          scope.classId,
          scope.studentId!
        );
        return notes.map((note) => ({
          id: `general-notice-${note.createdAt?.toMillis?.() || Date.now()}`,
          content: note.content || '',
          noticeScope: (note as { scope?: string }).scope || 'all',
          date: note.date || '',
          fontSize: note.fontSize || 16,
          createdAt: note.createdAt,
          updatedAt: note.updatedAt
        }));
      },
      { force: options?.force }
    );
  },

  async loadIndividualNotices(
    scope: MemberPageCacheScope,
    options?: { force?: boolean }
  ): Promise<any[]> {
    return studentMemberPageCache.getOrFetch(
      scope,
      'individualNotices',
      async () => {
        const notes = await boardService.getIndividualNoticeNotesForMember(
          scope.userId,
          scope.classId,
          scope.studentId!
        );
        return notes.map((note) => ({
          id: `individual-notice-${note.createdAt?.toMillis?.() || Date.now()}`,
          content: note.content || '',
          date: note.date || '',
          fontSize: note.fontSize || 16,
          createdAt: note.createdAt,
          updatedAt: note.updatedAt
        }));
      },
      { force: options?.force }
    );
  },

  async loadItemCards(scope: MemberPageCacheScope, options?: { force?: boolean }): Promise<CachedItemCards> {
    return studentMemberPageCache.getOrFetch(
      scope,
      'itemCards',
      async () => {
        const itemCardService = new ItemCardService(scope.userId, scope.classId, scope.memberGroup);
        const itemCardSet = await itemCardService.loadItemCardSet();
        let allItemCards: any[] = [];
        if (itemCardSet) {
          allItemCards = [
            ...itemCardSet.levelupItem,
            ...(itemCardSet.assignmentItem || []),
            ...itemCardSet.storeItem
          ];
        }

        const studentCards = await itemCardService.getStudentItemCards(scope.studentId!);
        return { allItemCards, studentItemCards: studentCards, itemCardSet };
      },
      { force: options?.force }
    );
  }
};
